import { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import { Plus, Trash2, DollarSign, TrendingUp, Calendar, Eye, EyeOff } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DonationsPage = () => {
    const [donations, setDonations] = useState([]);
    const [members, setMembers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [formData, setFormData] = useState({
        donor_name: '',
        amount: '',
        email: '',
        member_id: '',
        notes: '',
        is_anonymous: false
    });

    useEffect(() => {
        fetchDonations();
        fetchMembers();
    }, []);

    const fetchDonations = async () => {
        try {
            const response = await axios.get(`${API}/donations`);
            setDonations(response.data);
        } catch (error) {
            toast.error('Failed to load donations');
        } finally {
            setLoading(false);
        }
    };

    const fetchMembers = async () => {
        try {
            const response = await axios.get(`${API}/members`);
            setMembers(response.data);
        } catch (error) {
            console.error('Failed to load members');
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            await axios.post(`${API}/donations`, {
                ...formData,
                amount: parseFloat(formData.amount),
                member_id: formData.member_id === 'none' ? null : formData.member_id || null
            });
            toast.success('Donation recorded successfully');
            setIsModalOpen(false);
            resetForm();
            fetchDonations();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to save donation');
        }
    };

    const handleDelete = async (donationId) => {
        if (!window.confirm('Are you sure you want to delete this donation record?')) return;
        try {
            await axios.delete(`${API}/donations/${donationId}`);
            toast.success('Donation deleted successfully');
            fetchDonations();
        } catch (error) {
            toast.error('Failed to delete donation');
        }
    };

    const resetForm = () => {
        setFormData({
            donor_name: '',
            amount: '',
            email: '',
            member_id: '',
            notes: '',
            is_anonymous: false
        });
    };

    const totalDonations = donations.reduce((sum, d) => sum + d.amount, 0);
    const avgDonation = donations.length > 0 ? totalDonations / donations.length : 0;
    const thisMonth = donations.filter(d => {
        const donationDate = new Date(d.created_at);
        const now = new Date();
        return donationDate.getMonth() === now.getMonth() && donationDate.getFullYear() === now.getFullYear();
    });
    const thisMonthTotal = thisMonth.reduce((sum, d) => sum + d.amount, 0);

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-pulse text-[#A0A0A0]">Loading donations...</div>
            </div>
        );
    }

    return (
        <div className="space-y-6 animate-fade-in" data-testid="donations-page">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="text-4xl font-bold text-white font-['Playfair_Display'] tracking-tight">
                        Donations
                    </h1>
                    <p className="text-[#A0A0A0] mt-1">
                        Track and manage community contributions
                    </p>
                </div>
                <Dialog open={isModalOpen} onOpenChange={(open) => {
                    setIsModalOpen(open);
                    if (!open) resetForm();
                }}>
                    <DialogTrigger asChild>
                        <Button 
                            className="bg-[#C9A227] hover:bg-[#B8922A] text-white"
                            data-testid="add-donation-btn"
                        >
                            <Plus className="w-4 h-4 mr-2" />
                            Record Donation
                        </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-md">
                        <DialogHeader>
                            <DialogTitle className="text-2xl font-bold text-white font-['Playfair_Display']">
                                Record New Donation
                            </DialogTitle>
                        </DialogHeader>
                        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                            <div className="space-y-2">
                                <Label htmlFor="donor_name">Donor Name *</Label>
                                <Input
                                    id="donor_name"
                                    value={formData.donor_name}
                                    onChange={(e) => setFormData({ ...formData, donor_name: e.target.value })}
                                    required
                                    placeholder="John Doe"
                                    data-testid="donation-name-input"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="amount">Amount ($) *</Label>
                                <Input
                                    id="amount"
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    value={formData.amount}
                                    onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                                    required
                                    placeholder="100.00"
                                    data-testid="donation-amount-input"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="email">Email</Label>
                                <Input
                                    id="email"
                                    type="email"
                                    value={formData.email}
                                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                    placeholder="donor@email.com"
                                    data-testid="donation-email-input"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="member">Link to Member (optional)</Label>
                                <Select
                                    value={formData.member_id}
                                    onValueChange={(value) => setFormData({ ...formData, member_id: value })}
                                >
                                    <SelectTrigger data-testid="donation-member-select">
                                        <SelectValue placeholder="Select a member" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="none">No member link</SelectItem>
                                        {members.map((member) => (
                                            <SelectItem key={member.id} value={member.id}>
                                                {member.name}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="notes">Notes</Label>
                                <Textarea
                                    id="notes"
                                    value={formData.notes}
                                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                                    placeholder="Any additional notes..."
                                    rows={2}
                                    data-testid="donation-notes-input"
                                />
                            </div>
                            <div className="flex items-center gap-3">
                                <Switch
                                    id="is_anonymous"
                                    checked={formData.is_anonymous}
                                    onCheckedChange={(checked) => setFormData({ ...formData, is_anonymous: checked })}
                                    data-testid="donation-anonymous-switch"
                                />
                                <Label htmlFor="is_anonymous">Mark as Anonymous</Label>
                            </div>
                            <div className="flex justify-end gap-3 pt-4">
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={() => {
                                        setIsModalOpen(false);
                                        resetForm();
                                    }}
                                >
                                    Cancel
                                </Button>
                                <Button 
                                    type="submit"
                                    className="bg-[#C9A227] hover:bg-[#B8922A] text-white"
                                    data-testid="save-donation-btn"
                                >
                                    Record Donation
                                </Button>
                            </div>
                        </form>
                    </DialogContent>
                </Dialog>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                <Card className="border-[#5A6350] shadow-sm" data-testid="total-donations-stat">
                    <CardContent className="p-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-[#A0A0A0] font-medium">Total Raised</p>
                                <p className="text-3xl font-bold text-white mt-2 font-['Playfair_Display']">
                                    ${totalDonations.toLocaleString()}
                                </p>
                                <p className="text-sm text-[#A0A0A0] mt-1">{donations.length} donations</p>
                            </div>
                            <div className="bg-[#4A6B53] p-3 rounded-xl text-white">
                                <DollarSign className="w-6 h-6" />
                            </div>
                        </div>
                    </CardContent>
                </Card>
                <Card className="border-[#5A6350] shadow-sm" data-testid="avg-donation-stat">
                    <CardContent className="p-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-[#A0A0A0] font-medium">Average Donation</p>
                                <p className="text-3xl font-bold text-white mt-2 font-['Playfair_Display']">
                                    ${avgDonation.toFixed(2)}
                                </p>
                                <p className="text-sm text-[#A0A0A0] mt-1">Per contribution</p>
                            </div>
                            <div className="bg-[#C9A227] p-3 rounded-xl text-white">
                                <TrendingUp className="w-6 h-6" />
                            </div>
                        </div>
                    </CardContent>
                </Card>
                <Card className="border-[#5A6350] shadow-sm" data-testid="month-donations-stat">
                    <CardContent className="p-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-[#A0A0A0] font-medium">This Month</p>
                                <p className="text-3xl font-bold text-white mt-2 font-['Playfair_Display']">
                                    ${thisMonthTotal.toLocaleString()}
                                </p>
                                <p className="text-sm text-[#A0A0A0] mt-1">{thisMonth.length} donations</p>
                            </div>
                            <div className="bg-[#C48A54] p-3 rounded-xl text-white">
                                <Calendar className="w-6 h-6" />
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Donations Table */}
            <Card className="border-[#5A6350] shadow-sm" data-testid="donations-table-card">
                <CardHeader>
                    <CardTitle className="text-xl font-semibold text-white font-['Playfair_Display']">
                        Donation History
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    {donations.length > 0 ? (
                        <div className="overflow-x-auto">
                            <table className="data-table">
                                <thead>
                                    <tr>
                                        <th>Donor</th>
                                        <th>Amount</th>
                                        <th>Date</th>
                                        <th>Notes</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {donations.map((donation) => (
                                        <tr key={donation.id} data-testid={`donation-row-${donation.id}`}>
                                            <td>
                                                <div className="flex items-center gap-2">
                                                    {donation.is_anonymous ? (
                                                        <>
                                                            <EyeOff className="w-4 h-4 text-[#A0A0A0]" />
                                                            <span className="text-[#A0A0A0] italic">Anonymous</span>
                                                        </>
                                                    ) : (
                                                        <span className="font-medium text-white">{donation.donor_name}</span>
                                                    )}
                                                </div>
                                            </td>
                                            <td>
                                                <span className="font-semibold text-[#4A6B53]">
                                                    ${donation.amount.toLocaleString()}
                                                </span>
                                            </td>
                                            <td className="text-[#A0A0A0]">
                                                {new Date(donation.created_at).toLocaleDateString()}
                                            </td>
                                            <td className="text-[#A0A0A0] max-w-xs truncate">
                                                {donation.notes || '-'}
                                            </td>
                                            <td>
                                                <button
                                                    onClick={() => handleDelete(donation.id)}
                                                    className="p-2 hover:bg-red-50 rounded-lg transition-colors"
                                                    data-testid={`delete-donation-${donation.id}`}
                                                >
                                                    <Trash2 className="w-4 h-4 text-[#A64444]" />
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <div className="text-center py-12">
                            <DollarSign className="w-16 h-16 text-[#E6E1D8] mx-auto mb-4" />
                            <h3 className="text-lg font-semibold text-white mb-2">No donations yet</h3>
                            <p className="text-[#A0A0A0] mb-4">Start recording community contributions</p>
                            <Button 
                                onClick={() => setIsModalOpen(true)}
                                className="bg-[#C9A227] hover:bg-[#B8922A] text-white"
                            >
                                <Plus className="w-4 h-4 mr-2" />
                                Record Donation
                            </Button>
                        </div>
                    )}
                </CardContent>
            </Card>
        </div>
    );
};

export default DonationsPage;
